const ChannelsInfos = () => {
    return (
        <div className="mx-auto max-w-4xl px-6 py-8 text-gray-800 dark:text-gray-100">
            <h1 className="mb-6 text-3xl font-bold">
                Comment choisir mon canal d'acquisition ?
            </h1>

            <p className="mb-4">
                Choisir le bon canal d'acquisition dépend de plusieurs facteurs
                : vos objectifs commerciaux, la qualité des leads recherchée,
                votre budget et le volume de leads nécessaire. Voici un aperçu
                des différents canaux d'acquisition.
            </p>

            <p className="mb-4">
                Le système d’étoiles ⭐ vous permet de visualiser en un coup
                d’œil l’intérêt global d’un canal :
            </p>

            <p className="mb-8">
                <strong>1 étoile signifie peu intéressant</strong>, tandis que{' '}
                <strong>5 étoiles indiquent une excellente opportunité</strong>{' '}
                en termes de volume, qualité ou prix.
            </p>

            {[
                {
                    title: 'Google Ads',
                    description:
                        'Google Ads permet de promouvoir vos produits ou services via des annonces sponsorisées sur Google, basées sur des mots-clés spécifiques. Vous payez principalement par clic (CPC).',
                    reason: "Vous atteignez des prospects avec une forte intention d'achat, car ils recherchent activement ce que vous proposez. C'est un canal rapide et très efficace pour générer des leads qualifiés.",
                    evaluation: { volume: 3, quality: 5, price: 1 },
                },
                {
                    title: 'Bing Ads',
                    description:
                        'Similaire à Google Ads, Bing Ads vous permet de promouvoir vos annonces sur le moteur de recherche Bing.',
                    reason: 'Moins de concurrence que sur Google, ce qui peut entraîner des coûts plus bas tout en atteignant un public similaire.',
                    evaluation: { volume: 3, quality: 4, price: 3 },
                },
                {
                    title: 'Taboola',
                    description:
                        'Taboola permet de placer des annonces natives sous forme de recommandations de contenu sur des sites partenaires.',
                    reason: 'Il attire un public large via un format discret et non intrusif. Cela peut être très efficace pour augmenter la visibilité et générer du trafic.',
                    evaluation: { volume: 4, quality: 2, price: 3 },
                },
                {
                    title: 'Facebook',
                    description:
                        'Facebook Ads permet de cibler des utilisateurs très spécifiques via des critères démographiques et comportementaux.',
                    reason: "Très populaire et très ciblé, ce canal permet d'atteindre une audience large et segmentée.",
                    evaluation: { volume: 4, quality: 3, price: 3 },
                },
                {
                    title: 'Instagram',
                    description:
                        'Instagram Ads est une plateforme visuelle permettant de toucher une audience large, efficace pour des produits visuels.',
                    reason: "Très populaire chez une audience jeune, c'est un excellent moyen de capter l'attention avec des formats créatifs.",
                    evaluation: { volume: 5, quality: 3, price: 3 },
                },
                {
                    title: 'Twitter',
                    description:
                        'Twitter Ads permet de promouvoir des tweets sponsorisés.',
                    reason: 'Idéal pour les actualités ou campagnes en temps réel, mais moins efficace pour des cycles de vente longs.',
                    evaluation: { volume: 4, quality: 3, price: 3 },
                },
                {
                    title: 'LinkedIn',
                    description:
                        'LinkedIn Ads est particulièrement efficace pour le B2B.',
                    reason: 'Idéal pour l’acquisition de leads qualifiés dans un contexte professionnel.',
                    evaluation: { volume: 3, quality: 5, price: 4 },
                },
                {
                    title: 'Pinterest',
                    description:
                        'Pinterest permet de promouvoir des épingles sponsorisées, efficace pour les produits visuels.',
                    reason: 'Très visuel, Pinterest attire une audience inspirée, idéale pour mode, déco ou DIY.',
                    evaluation: { volume: 3, quality: 3, price: 3 },
                },
                {
                    title: 'SEO (Référencement naturel)',
                    description:
                        "Le SEO consiste à optimiser votre site web pour qu'il apparaisse dans les résultats organiques.",
                    reason: 'Attirer des leads très qualifiés avec un investissement long terme et un excellent ROI.',
                    evaluation: { volume: 1, quality: 5, price: 5 },
                },
                {
                    title: 'Emailing',
                    description:
                        'L’emailing consiste à envoyer des emails ciblés à une base de données.',
                    reason: "Rentable pour maintenir l'engagement et convertir, dépend fortement de la qualité de la base.",
                    evaluation: { volume: 5, quality: 2, price: 4 },
                },
                {
                    title: 'SMS',
                    description:
                        'Le marketing par SMS consiste à envoyer des messages courts à vos prospects.',
                    reason: "Taux d'ouverture élevé, parfait pour les promotions urgentes ou rappels.",
                    evaluation: { volume: 4, quality: 2, price: 3 },
                },
                {
                    title: 'Newsletter',
                    description:
                        'Les newsletters sont des emails réguliers envoyés pour informer et fidéliser.',
                    reason: "Maintient l'engagement et la fidélité, dépendant de la base d'abonnés.",
                    evaluation: { volume: 4, quality: 1, price: 5 },
                },
                {
                    title: 'Display (Bannières publicitaires)',
                    description:
                        'Les publicités display sont des bannières visuelles visibles sur des sites partenaires.',
                    reason: 'Parfait pour la notoriété, mais conversion plus faible car format intrusif.',
                    evaluation: { volume: 5, quality: 2, price: 3 },
                },
                {
                    title: 'TikTok',
                    description:
                        'TikTok permet de créer des publicités vidéos courtes engageantes.',
                    reason: 'Canal en essor avec fort engagement, idéal pour campagnes visuelles.',
                    evaluation: { volume: 5, quality: 3, price: 3 },
                },
                {
                    title: 'Robot IA (Chatbots, Automatisation)',
                    description:
                        "Les robots IA permettent d'interagir directement avec les visiteurs pour qualifier les leads.",
                    reason: 'Capte les leads en temps réel, réduit les coûts humains, très efficace.',
                    evaluation: { volume: 4, quality: 4, price: 3 },
                },
            ].map((channel, index) => (
                <div key={index} className="mb-10">
                    <h2 className="mb-2 text-xl font-bold">
                        {index + 1}. {channel.title}
                    </h2>
                    <p className="mb-2">
                        <strong>Description</strong> : {channel.description}
                    </p>
                    <p className="mb-2">
                        <strong>Pourquoi c'est intéressant ?</strong> :{' '}
                        {channel.reason}
                    </p>
                    <p className="mb-2">
                        <strong>Évaluation</strong> :
                    </p>
                    <ul className="list-disc pl-6">
                        <li>
                            <strong>Volume</strong> :{' '}
                            {'⭐'.repeat(channel.evaluation.volume)}
                        </li>
                        <li>
                            <strong>Qualité</strong> :{' '}
                            {'⭐'.repeat(channel.evaluation.quality)}
                        </li>
                        <li>
                            <strong>Prix</strong> :{' '}
                            {'⭐'.repeat(channel.evaluation.price)}
                        </li>
                    </ul>
                </div>
            ))}
        </div>
    );
};

export default ChannelsInfos;
